import { useCallback } from "react";
import { useNavigate } from "react-router";

import { canAccessPropertiesNav } from "@/components/Sidebar/navRoleAccess";
import useStore from "@/hooks/useStore";
import PropertyBackgroundSync from "@/store/PropertyBackgroundSync";
import DefaultStore from "@/store/Store";
import { getDefaultRoute } from "@/routes/loaders";

/** Shared across all hook instances so layout + page callers coordinate activeRole skip. */
const skipNextActiveRoleIdentityChangeRef = { current: false };

/**
 * Re-warm property list after impersonation reset. Identity deps often already flushed during
 * `await startImpersonation` / `stopImpersonation`, so wrappers will not call setActiveCustomer again.
 */
function warmPropertiesAfterIdentityChange(authStore) {
  if (!canAccessPropertiesNav(authStore.role)) {
    PropertyBackgroundSync.stop();
    return;
  }
  PropertyBackgroundSync.start(DefaultStore);
  PropertyBackgroundSync.setActiveCustomer(
    authStore.customer ?? null,
    authStore.userId ?? null,
    authStore.currentRole?.() ?? authStore.role ?? null
  );
}

/**
 * Superadmin impersonation start/stop with domain-store reset and default-route navigation.
 * Also exposes a ref so layout code can skip the duplicate activeRole identity pass.
 *
 * @returns {{
 *   handleActingUserChange: (selection: { userId?: string, email?: string, displayName?: string, roles?: string[] } | null) => Promise<void>,
 *   skipNextActiveRoleIdentityChangeRef: import("react").MutableRefObject<boolean>,
 * }}
 */
export default function useActingUserChange() {
  const navigate = useNavigate();
  const { authStore } = useStore();

  const handleActingUserChange = useCallback(
    async (selection) => {
      skipNextActiveRoleIdentityChangeRef.current = true;
      if (selection == null) {
        await authStore.stopImpersonation();
      } else {
        await authStore.startImpersonation(selection);
      }
      DefaultStore.onIdentityChange();
      warmPropertiesAfterIdentityChange(authStore);
      navigate(getDefaultRoute(authStore), { replace: true });
      void Promise.all([authStore.fetchFiles(), authStore.fetchAgents()]).catch(() => {});
    },
    [authStore, navigate]
  );

  return { handleActingUserChange, skipNextActiveRoleIdentityChangeRef };
}
